// ClipRadar — the closing moment: THE PING (founder-picked 2026-07-17, replacing
// the disc monument). The radar performed as a sunrise: instrument rings
// half-risen behind the horizon line, a ping wave born from the line every few
// seconds, echo blips answering where clips are found — the held ones resolve
// into tiny 16:9 clip frames. Interactive: the dial leans toward the cursor,
// nearby echoes stir, and a click/tap fires a ping whose wave lights up the
// clicked spot. The first wave DRAWS the dial (ignite choreography).
// All light rides the --light-* glacier family; everything is inline + GSAP
// (opacity/transform/SVG-attr only — no CSS file, no keyframes).
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GradBtn, MonoNote } from '../components/primitives';
import { useSignedIn } from '../components/hooks';

gsap.registerPlugin(ScrollTrigger);

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const PING = { H: 380, LINE: 84, CX: 400, CY: 320, RINGS: [60, 116, 180, 252, 316] };

// echo positions (deg from the right horizon, radius px) in three groups —
// each ping cycle answers with a different group, two echoes per group persist
const PING_BLIPS: { a: number; r: number; g: number }[] = [
  { a: 38, r: 150, g: 0 }, { a: 96, r: 240, g: 0 }, { a: 142, r: 92, g: 0 },
  { a: 63, r: 200, g: 1 }, { a: 118, r: 286, g: 1 }, { a: 27, r: 116, g: 1 },
  { a: 78, r: 124, g: 2 }, { a: 104, r: 182, g: 2 }, { a: 152, r: 256, g: 2 },
];
const blipXY = (b: { a: number; r: number }) => ({
  x: PING.CX + b.r * Math.cos((b.a * Math.PI) / 180),
  y: PING.CY - b.r * Math.sin((b.a * Math.PI) / 180),
});

function Radar() {
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = boxRef.current!;
    const svg = box.querySelector<SVGSVGElement>('svg')!;
    const srings = Array.from(box.querySelectorAll<SVGCircleElement>('.cpv-sring'));
    const ticksBig = Array.from(box.querySelectorAll<SVGLineElement>('.cpv-tick-b'));
    const ticksSmall = Array.from(box.querySelectorAll<SVGLineElement>('.cpv-tick-s'));
    const ring = box.querySelector<SVGCircleElement>('.cpv-ring')!;
    const ringGlow = box.querySelector<SVGCircleElement>('.cpv-ring-glow')!;
    const core = box.querySelector<SVGCircleElement>('.cpv-core')!;
    const lineFlash = box.querySelector<HTMLElement>('.cpv-lineflash')!;
    const blipEls = Array.from(box.querySelectorAll<SVGGElement>('.cpv-blip'));
    const uecho = box.querySelector<SVGGElement>('.cpv-uecho')!;
    const RING_OP = [0.38, 0.28, 0.2, 0.15, 0.11];

    if (reduceMotion()) {
      // still life: the full instrument with three resolved clips
      srings.forEach((el, i) => { el.style.opacity = String(RING_OP[i]); });
      ticksBig.forEach((el) => { el.style.opacity = '0.44'; });
      ticksSmall.forEach((el) => { el.style.opacity = '0.3'; });
      [0, 4, 7].forEach((i) => {
        blipEls[i].style.opacity = '0.6';
        blipEls[i].querySelector<SVGRectElement>('.cpv-blip-frame')!.style.opacity = '0.8';
      });
      return;
    }

    let active = false;
    let cycle = 0;
    let persisted: SVGGElement[] = [];
    const persistedSet = new Set<SVGGElement>();
    const currentMembers = new Set<SVGGElement>();
    let tl: gsap.core.Timeline | null = null;
    let next: gsap.core.Tween | null = null;

    const hitAt = (r: number) => 0.22 + 3.4 * ((r - 10) / 322);

    // schedules one echo (flare + ripple + optional resolve-into-clip) on the timeline
    const scheduleEcho = (el: SVGGElement, r: number, hold: boolean) => {
      const ripple = el.querySelector<SVGCircleElement>('.cpv-blip-ripple')!;
      const frame = el.querySelector<SVGRectElement>('.cpv-blip-frame')!;
      const hit = hitAt(r);
      tl!.to(el, { opacity: 1, duration: 0.18, ease: 'power2.out' }, hit);
      tl!.set(ripple, { attr: { r: 4 }, opacity: 0.7 }, hit);
      tl!.to(ripple, { attr: { r: 19 }, opacity: 0, duration: 0.7, ease: 'power2.out' }, hit + 0.05);
      tl!.to(el, { opacity: hold ? 0.6 : 0, duration: 1.3, ease: 'power2.inOut' }, hit + 0.25);
      if (hold) {
        tl!.to(frame, { opacity: 0.8, duration: 0.5, ease: 'power2.out' }, hit + 0.9);
        persisted.push(el);
        persistedSet.add(el);
      }
    };

    const ping = (user?: { r: number }) => {
      if (!active) return;
      const group = cycle % 3;
      const first = cycle === 0;
      cycle += 1;
      tl = gsap.timeline({ onComplete: () => { next = gsap.delayedCall(1.9 + (cycle % 3) * 0.4, () => ping()); } });
      // a fresh wave claims the stage: everything not legitimately held goes dark.
      // The authority is the persisted ARRAY — a killed cycle can leave the set
      // stale and its half-faded holds frozen (bit us: 5 lit echoes after a click).
      currentMembers.clear();
      persistedSet.clear();
      persisted.forEach((el) => persistedSet.add(el));
      [...blipEls, uecho].forEach((el) => {
        if (!persisted.includes(el)) {
          tl!.set(el, { opacity: 0 }, 0);
          tl!.set(el.querySelector('.cpv-blip-frame'), { opacity: 0 }, 0);
        }
      });
      // anticipation — then the wave is born out of the line itself
      tl.to(core, { attr: { r: 6.5 }, duration: 0.22, ease: 'power2.out' }, 0);
      tl.to(core, { attr: { r: 4 }, duration: 0.4, ease: 'power2.inOut' }, 0.24);
      tl.fromTo(lineFlash, { opacity: 0 }, { opacity: 0.9, duration: 0.16, ease: 'power2.out' }, 0.18);
      tl.to(lineFlash, { opacity: 0, duration: 0.9, ease: 'power2.inOut' }, 0.5);
      tl.set([ring, ringGlow], { attr: { r: 10 } }, 0.22);
      tl.set(ring, { opacity: 0.85 }, 0.22);
      tl.set(ringGlow, { opacity: 0.65 }, 0.22);
      tl.to([ring, ringGlow], { attr: { r: 332 }, duration: 3.4, ease: 'none' }, 0.22);
      tl.to([ring, ringGlow], { opacity: 0, duration: 0.75, ease: 'power1.in' }, 2.95);
      // the first wave draws the instrument as it travels outward
      if (first) {
        srings.forEach((el, i) => tl!.to(el, { opacity: RING_OP[i], duration: 0.5, ease: 'power2.out' }, Math.max(0.1, hitAt(PING.RINGS[i]) - 0.12)));
        tl.to(ticksSmall, { opacity: 0.3, duration: 0.5, ease: 'power2.out', stagger: 0.04 }, hitAt(PING.RINGS[4]) - 0.2);
        tl.to(ticksBig, { opacity: 0.44, duration: 0.5, ease: 'power2.out', stagger: 0.06 }, hitAt(PING.RINGS[4]) - 0.2);
      }
      // the previous cycle's held clips let go
      persisted.forEach((el) => {
        tl!.to(el, { opacity: 0, duration: 0.8, ease: 'power2.inOut' }, 0.3);
        tl!.to(el.querySelector('.cpv-blip-frame'), { opacity: 0, duration: 0.8, ease: 'power2.inOut' }, 0.3);
        tl!.call(() => { persistedSet.delete(el); }, undefined, 1.2);
      });
      persisted = [];
      // echoes flare as the wave reaches them; the last two resolve into clips
      const members = PING_BLIPS.map((b, i) => ({ b, i })).filter(({ b }) => b.g === group);
      members.forEach(({ b, i }, k) => {
        currentMembers.add(blipEls[i]);
        scheduleEcho(blipEls[i], b.r, k >= members.length - 2);
      });
      // the spot the user asked about always answers, and always resolves
      if (user) {
        currentMembers.add(uecho);
        scheduleEcho(uecho, user.r, true);
      }
    };

    const io = new IntersectionObserver((entries) => {
      const vis = entries.some((e) => e.isIntersecting);
      if (vis && !active) {
        active = true;
        if (!tl || !tl.isActive()) { next?.kill(); next = gsap.delayedCall(0.35, () => ping()); }
      } else if (!vis && active) {
        active = false; // the running ping finishes; no new one is scheduled
      }
    }, { threshold: 0.3 });
    io.observe(box);

    // viewport px -> svg viewBox coords (the svg is centered with margins, no transform)
    const toSvg = (clientX: number, clientY: number) => {
      const r = svg.getBoundingClientRect();
      return { x: ((clientX - r.left) / r.width) * 800, y: ((clientY - r.top) / r.height) * 330 };
    };

    // click / tap: ping NOW, and the clicked spot answers when the wave arrives
    const onDown = (e: PointerEvent) => {
      if (!active) return;
      const { x, y } = toSvg(e.clientX, e.clientY);
      const r = Math.hypot(x - PING.CX, PING.CY - y);
      if (y > PING.CY - 6 || r < 30 || r > 330) return; // only the sky answers
      ['.cpv-user-halo', '.cpv-blip-ripple', '.cpv-user-dot'].forEach((sel) => {
        const c = uecho.querySelector<SVGCircleElement>(sel)!;
        c.setAttribute('cx', String(x)); c.setAttribute('cy', String(y));
      });
      const fr = uecho.querySelector<SVGRectElement>('.cpv-blip-frame')!;
      fr.setAttribute('x', String(x - 9.5)); fr.setAttribute('y', String(y - 6));
      persistedSet.delete(uecho);
      persisted = persisted.filter((el) => el !== uecho);
      next?.kill();
      tl?.kill();
      gsap.set([ring, ringGlow, lineFlash], { opacity: 0 });
      gsap.set(uecho, { opacity: 0 });
      ping({ r });
    };
    box.addEventListener('pointerdown', onDown);

    // hover: the dial leans toward the cursor; nearby echoes stir (fine pointers only)
    let raf = 0;
    let mx = -9999, my = -9999;
    const applyHover = () => {
      raf = 0;
      const r = svg.getBoundingClientRect();
      PING_BLIPS.forEach((b, i) => {
        const el = blipEls[i];
        if (currentMembers.has(el) && tl?.isActive()) return; // the wave owns it right now
        const { x, y } = blipXY(b);
        const cx = r.left + (x / 800) * r.width;
        const cy = r.top + (y / 330) * r.height;
        const d = Math.hypot(mx - cx, my - cy);
        const boost = Math.max(0, 1 - d / 130) ** 2;
        el.style.opacity = String(persistedSet.has(el) ? Math.min(1, 0.6 + boost * 0.4) : boost * 0.3);
      });
    };
    const onMove = (e: PointerEvent) => {
      mx = e.clientX; my = e.clientY;
      const r = box.getBoundingClientRect();
      gsap.to(svg, {
        x: ((e.clientX - r.left) / r.width - 0.5) * 16,
        y: ((e.clientY - r.top) / r.height - 0.5) * 9,
        duration: 0.9, ease: 'power2.out', overwrite: 'auto',
      });
      if (!raf) raf = requestAnimationFrame(applyHover);
    };
    const onLeave = () => {
      mx = -9999; my = -9999;
      gsap.to(svg, { x: 0, y: 0, duration: 1.1, ease: 'power2.out', overwrite: 'auto' });
      if (!raf) raf = requestAnimationFrame(applyHover);
    };
    const wireCursor = finePointer();
    if (wireCursor) {
      box.addEventListener('pointermove', onMove);
      box.addEventListener('pointerleave', onLeave);
    }

    return () => {
      io.disconnect();
      tl?.kill();
      next?.kill();
      box.removeEventListener('pointerdown', onDown);
      if (wireCursor) { box.removeEventListener('pointermove', onMove); box.removeEventListener('pointerleave', onLeave); }
      if (raf) cancelAnimationFrame(raf);
      gsap.killTweensOf(svg);
    };
  }, []);

  // Stage width must respect the padded parent — the old 94vw overflowed it on
  // phones and pushed the dial ~12px right of viewport center (founder repro).
  // Height tracks the svg's real rendered height (0.4125 × width) + line offset
  // so the stage sheds its dead sky on narrow screens; the min() cap restores
  // exactly the desktop 380px from ~850px viewport width up.
  return (
    <div ref={boxRef} style={{ position: 'relative', width: 'min(980px, 100%)', height: `min(${PING.H}px, calc(41.25vw + 30px))`, margin: '0 auto', cursor: 'crosshair', touchAction: 'manipulation' }}>
      {/* the horizon the radar rises behind — the glow blooms UP from the line */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', bottom: PING.LINE, width: 0, height: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', left: 0, bottom: 0, transform: 'translateX(-50%)', width: '170vw', maxWidth: 2400, height: 440, background: 'radial-gradient(ellipse 52% 62% at 50% 100%, rgba(var(--light-rgb),0.34), rgba(var(--light-deep-rgb),0.13) 42%, rgba(var(--light-deep-rgb),0.04) 62%, transparent 74%)' }}></div>
        <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translateX(-50%)', width: '150vw', maxWidth: 2000, height: 240, background: 'radial-gradient(ellipse 50% 46% at 50% 0%, rgba(var(--light-rgb),0.10), transparent 70%)' }}></div>
        <div style={{ position: 'absolute', left: 0, top: -1, transform: 'translateX(-50%)', width: 'min(1200px, 94vw)', height: 2, borderRadius: 2, background: 'linear-gradient(90deg, transparent, rgba(var(--light-high-rgb),0.8) 32%, rgb(var(--light-high-rgb)) 50%, rgba(var(--light-high-rgb),0.8) 68%, transparent)', boxShadow: '0 0 26px rgba(var(--light-rgb),0.6), 0 0 100px rgba(var(--light-rgb),0.35)' }}></div>
      </div>

      {/* centered with margins — NOT a transform, so the parallax tween composes cleanly */}
      <svg
        viewBox="0 0 800 330" aria-hidden="true"
        style={{ position: 'absolute', left: 0, right: 0, margin: '0 auto', bottom: PING.LINE - 2, width: 'min(800px, 100%)', height: 'auto', overflow: 'visible' }}
      >
        <defs>
          <clipPath id="cpv-sky"><rect x="-40" y="-20" width="880" height="340" /></clipPath>
          <radialGradient id="cpv-bloom" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" style={{ stopColor: 'var(--light-core)' }} stopOpacity="0.9" />
            <stop offset="1" style={{ stopColor: 'rgb(var(--light-high-rgb))' }} stopOpacity="0" />
          </radialGradient>
          <filter id="cpv-b5" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="5" /></filter>
        </defs>

        <g clipPath="url(#cpv-sky)">
          {/* the instrument: etched rings, drawn by the first wave */}
          {PING.RINGS.map((r) => (
            <circle key={r} className="cpv-sring" cx={PING.CX} cy={PING.CY} r={r} fill="none"
              style={{ stroke: 'rgb(var(--light-mid-rgb))', opacity: 0 }} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          {/* dial graduations on the outer ring — built like an instrument */}
          {Array.from({ length: 11 }, (_, i) => 15 + i * 15).map((deg) => {
            const rad = (deg * Math.PI) / 180;
            const big = deg % 45 === 0;
            const r0 = PING.RINGS[4] - (big ? 12 : 7);
            const r1 = PING.RINGS[4] - 2;
            return (
              <line key={deg} className={big ? 'cpv-tick-b' : 'cpv-tick-s'}
                x1={PING.CX + r0 * Math.cos(rad)} y1={PING.CY - r0 * Math.sin(rad)}
                x2={PING.CX + r1 * Math.cos(rad)} y2={PING.CY - r1 * Math.sin(rad)}
                style={{ stroke: 'rgb(var(--light-mid-rgb))', opacity: 0 }} strokeWidth={big ? 1.6 : 1.2} vectorEffect="non-scaling-stroke" />
            );
          })}
        </g>

        {/* echoes — clips answering the ping; held ones resolve into frames */}
        {PING_BLIPS.map((b, i) => {
          const { x, y } = blipXY(b);
          return (
            <g key={i} className="cpv-blip" style={{ opacity: 0 }}>
              <circle cx={x} cy={y} r="13" fill="url(#cpv-bloom)" opacity="0.75" />
              <circle className="cpv-blip-ripple" cx={x} cy={y} r="4" fill="none" style={{ stroke: 'rgb(var(--light-high-rgb))', opacity: 0 }} strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <rect className="cpv-blip-frame" x={x - 9.5} y={y - 6} width="19" height="12" rx="2.5" fill="none" style={{ stroke: 'rgb(var(--light-high-rgb))', opacity: 0 }} strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <circle cx={x} cy={y} r="2.4" style={{ fill: 'var(--light-core)' }} />
            </g>
          );
        })}

        {/* the user's echo — wherever they click, the radar answers there */}
        <g className="cpv-uecho" style={{ opacity: 0 }}>
          <circle className="cpv-user-halo" cx="-100" cy="-100" r="13" fill="url(#cpv-bloom)" opacity="0.75" />
          <circle className="cpv-blip-ripple" cx="-100" cy="-100" r="4" fill="none" style={{ stroke: 'rgb(var(--light-high-rgb))', opacity: 0 }} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <rect className="cpv-blip-frame" x="-110" y="-106" width="19" height="12" rx="2.5" fill="none" style={{ stroke: 'rgb(var(--light-high-rgb))', opacity: 0 }} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <circle className="cpv-user-dot" cx="-100" cy="-100" r="2.4" style={{ fill: 'var(--light-core)' }} />
        </g>

        {/* the expanding ping — a crisp wave riding on its own glow */}
        <circle className="cpv-ring-glow" cx={PING.CX} cy={PING.CY} r="10" fill="none" clipPath="url(#cpv-sky)"
          style={{ stroke: 'rgb(var(--light-rgb))', opacity: 0 }} strokeWidth="6" filter="url(#cpv-b5)" />
        <circle className="cpv-ring" cx={PING.CX} cy={PING.CY} r="10" fill="none" clipPath="url(#cpv-sky)"
          style={{ stroke: 'rgb(var(--light-high-rgb))', opacity: 0 }} strokeWidth="2" />

        {/* the source — the line's own energy, gathered to a point */}
        <circle cx={PING.CX} cy={PING.CY} r="14" fill="url(#cpv-bloom)" opacity="0.8" filter="url(#cpv-b5)" />
        <circle className="cpv-core" cx={PING.CX} cy={PING.CY} r="4" style={{ fill: 'var(--light-core)' }} />
      </svg>
      {/* the flash the wave is born from — rides ON the horizon line */}
      <div className="cpv-lineflash" aria-hidden="true" style={{ position: 'absolute', left: '50%', bottom: PING.LINE - 1, transform: 'translateX(-50%)', width: 260, height: 3, borderRadius: 3, opacity: 0, pointerEvents: 'none', background: 'linear-gradient(90deg, transparent, rgba(var(--light-high-rgb), 0.95), transparent)', boxShadow: '0 0 18px rgba(var(--light-rgb), 0.8)' }} />
    </div>
  );
}

export function FinalCTA() {
  const signedIn = useSignedIn(); // signed-in = already trialing → sell the upgrade
  return (
    <section style={{ position: 'relative', padding: '96px 24px 130px', overflow: 'hidden' }}>
      <div style={{ position: 'relative', maxWidth: 980, margin: '0 auto', textAlign: 'center' }}>
        <Radar />
        <h2 className="crw-h-grad" style={{ margin: '40px 0 0', fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 4.6vw, 58px)', fontWeight: 600, letterSpacing: 'var(--tracking-display)', lineHeight: 1.06 }}>
          Light up your library.
        </h2>
        <p style={{ margin: '18px auto 0', maxWidth: 480, fontSize: 16.5, lineHeight: 1.65, color: 'var(--fg-2)', textWrap: 'pretty' }}>
          Add a folder and ClipRadar will make every scene, word, and face searchable.
        </p>
        <div style={{ marginTop: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
          {signedIn ? (
            <a href="#pricing" style={{ textDecoration: 'none', display: 'flex' }}>
              <GradBtn size="lg">Buy ClipRadar</GradBtn>
            </a>
          ) : (
            <Link to="/trial" style={{ textDecoration: 'none', display: 'flex' }}>
              <GradBtn size="lg">Start Free Trial</GradBtn>
            </Link>
          )}
          <a href="#pricing" className="crw-glass crw-ghostbtn" style={{ textDecoration: 'none' }}>See pricing</a>
        </div>
        <div style={{ marginTop: 22 }}>
          {/* nbsp inside the product names: at 390px the 11->12px MonoNote bump
              started breaking this as "… PREMIERE / PRO PANEL INCLUDED", which
              splits a product name across lines (ui-critic, 2026-07-26). */}
          <MonoNote><span style={{ whiteSpace: 'nowrap' }}>Windows</span>{' '}<span style={{ whiteSpace: 'nowrap' }}>· NVIDIA GPU required</span>{' '}<span style={{ whiteSpace: 'nowrap' }}>· Premiere Pro panel included</span></MonoNote>
        </div>
      </div>
    </section>
  );
}
